import { type FormikErrors, type FormikTouched, useFormik } from 'formik'
import { useEffect, useRef } from 'react'
import * as Yup from 'yup'

import { Select } from '@loadsmart/loadsmart-ui'
import type { SelectDatasource } from '@loadsmart/loadsmart-ui/dist/components/Select/Select.types'
import { type Selectable } from '@loadsmart/loadsmart-ui/dist/hooks/useSelectable'
import type EventLike from '@loadsmart/loadsmart-ui/dist/utils/types/EventLike'
import { Drawer, Field, Icon, LabeledValue, Layout, Steps } from '@loadsmart/miranda-react'

import {
  type OpenDockLoadTypes,
  type ScheduleFormValues,
  getDockWithLowestSortOrder,
} from '../../domain/Shipment'
import { formatLoadTypeInfo } from '../../presenters/openDockLoadTypePresenter'
import { CustomOption } from './CustomOption'
import { ErrorMessage } from './ErrorMessage'
import { useOpenDockDrawer } from './OpenDockDrawerContext'
import { SignUpStatusAccordion } from './SignupStatusAccordion'

function buildLoadTypeDataSource(loadTypes: Array<OpenDockLoadTypes>) {
  return (): SelectDatasource<OpenDockLoadTypes> => {
    return {
      type: 'loadType',
      adapter: {
        getKey: item => item.id,
        getLabel: (item: OpenDockLoadTypes) => formatLoadTypeInfo(item, true),
      },
      fetch({ regex }) {
        return loadTypes.filter(({ name }) => regex.test(name))
      },
    }
  }
}

//change some string values to shape
const Schema = Yup.object().shape({
  loadType: Yup.object<OpenDockLoadTypes>().required('Required'),
  dock: Yup.object<{ value: string; label: string }>().required('Required'),
})

function getValidationStatus(
  fieldName: keyof ScheduleFormValues,
  errors: FormikErrors<ScheduleFormValues>,
  touched: FormikTouched<ScheduleFormValues>
) {
  if (touched[fieldName] && errors[fieldName]) {
    return 'danger'
  }

  return 'default'
}

export function Schedule() {
  const { carrierId, warehouseDetails, shipmentCarrier, setStep, fields, setStaticFields } =
    useOpenDockDrawer()

  const isSignedUp = Boolean(carrierId)

  const dialogBodyRef = useRef<React.ComponentRef<typeof Drawer.Body>>(null)

  const {
    handleChange,
    values,
    setFieldValue,
    errors,
    touched,
    setFieldTouched,
    isValid,
    validateField,
  } = useFormik<Partial<ScheduleFormValues>>({
    initialValues: {
      loadType: fields.staticFields.loadType,
      dock: fields.staticFields.dock,
    },
    onSubmit: () => undefined,
    validationSchema: Schema,
    validateOnMount: true,
  })

  const showDocks = Boolean(values.loadType?.id) && values.loadType?.allowCarrierDockSelection

  useEffect(() => {
    // this check is to make the button enable as soon as we get the values right
    // just because with allowCarrierDockSelection as false, we set the dock value programmatically.
    // Without this, a click outside the loadtype field is needed to trigger validation.
    if (values.loadType && values.dock && values.loadType.allowCarrierDockSelection === false) {
      validateField('dock')
      setFieldTouched('dock')
    }
  }, [values.loadType, values.dock, validateField, setFieldTouched])

  function handleChangeLoadType(event: EventLike<Selectable | Selectable[] | null>) {
    handleChange(event)

    if (
      event.target.value &&
      (event.target.value as OpenDockLoadTypes)?.docks.length > 0 &&
      (event.target.value as OpenDockLoadTypes).allowCarrierDockSelection === false
    ) {
      const docks = (event.target.value as OpenDockLoadTypes).docks
      const dockWithLowestSortOrder = getDockWithLowestSortOrder(docks)

      setFieldValue('dock', {
        value: dockWithLowestSortOrder.id,
        label: dockWithLowestSortOrder.name,
      })

      return
    }

    setFieldValue('dock', null)
  }

  return (
    <>
      <Drawer.Body ref={dialogBodyRef}>
        <Layout.Stack gap="spacing-4" paddingB="spacing-2">
          <Steps activeStep="schedule" style={{ gap: '0' }}>
            <Steps.Step name="schedule">Schedule</Steps.Step>
            <Steps.Step name="send-info">Send Info</Steps.Step>
          </Steps>
          <SignUpStatusAccordion shipmentCarrier={shipmentCarrier} isSignedUp={isSignedUp} />
          <Layout.Stack gap="spacing-4">
            <LabeledValue>
              <LabeledValue.Label>Warehouse</LabeledValue.Label>
              <LabeledValue.Value>{warehouseDetails.name}</LabeledValue.Value>
            </LabeledValue>
            <Field required status={getValidationStatus('loadType', errors, touched)}>
              <Field.Label>Load type</Field.Label>
              <Select
                name="loadType"
                placeholder="Select load type"
                datasources={[buildLoadTypeDataSource(warehouseDetails.loadTypes)]}
                value={values.loadType as Selectable | null}
                components={{
                  Option: CustomOption,
                }}
                onChange={handleChangeLoadType}
                onBlur={() => setFieldTouched('loadType')}
              ></Select>
              <ErrorMessage
                validationStatus={getValidationStatus('loadType', errors, touched)}
                errors={errors}
                fieldName="loadType"
              />
            </Field>
            {showDocks && (
              <Field required status={getValidationStatus('dock', errors, touched)}>
                <Field.Label>Docks</Field.Label>
                <Select
                  name="dock"
                  placeholder="Select docks"
                  value={values.dock as Selectable | null}
                  options={values.loadType?.docks.map(dock => ({
                    value: dock.id,
                    label: dock.name,
                  }))}
                  onChange={event => {
                    handleChange(event)
                  }}
                  onBlur={() => setFieldTouched('dock')}
                  disabled={!values.loadType}
                />
                <ErrorMessage
                  validationStatus={getValidationStatus('dock', errors, touched)}
                  errors={errors}
                  fieldName="dock"
                />
              </Field>
            )}
          </Layout.Stack>
        </Layout.Stack>
      </Drawer.Body>
      <Drawer.Actions>
        <Drawer.ActionTertiary
          onClick={() => setStep('instructions')}
          leading={<Icon name="arrow-left" color="color-text-primary" />}
        >
          Back
        </Drawer.ActionTertiary>
        <Drawer.ActionPrimary
          disabled={!isValid}
          trailing={<Icon name="arrow-right" color="color-text-inverted" />}
          onClick={() => {
            setStep('schedule_date_and_time')
            setStaticFields(values)
          }}
        >
          Next
        </Drawer.ActionPrimary>
      </Drawer.Actions>
    </>
  )
}
